import { CoinsIcon, PrinterIcon, ClockIcon } from "@phosphor-icons/react";
import { AdminDataTable, AdminTableIdentity } from "@bednbody/ui/components/admin-data-table";
import { StatTile } from "@bednbody/ui/components/stat-tile";
import { cn } from "@bednbody/ui/lib/utils";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import {
  cardsLabel,
  type GiftpassOrder,
  ORDER_FILTERS,
  paidBy,
  peso,
  STATUS_LABEL,
  STATUS_TONE,
} from "./giftpass-orders.model";
import { useGiftpassOrders } from "./giftpass-orders.hooks";
import { OrderDetail } from "./OrderDetail";

const when = (iso: string) =>
  new Date(iso).toLocaleString("en-PH", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const columns: ColumnDef<GiftpassOrder, unknown>[] = [
  {
    id: "order",
    accessorFn: (o) => o.order_code,
    header: "Order",
    cell: ({ row }) => (
      <AdminTableIdentity
        title={<span className="font-mono">{row.original.order_code}</span>}
        subtitle={row.original.source === "manual" ? "Manual" : "Online"}
        fallback="GP"
      />
    ),
  },
  {
    id: "date",
    accessorFn: (o) => o.created_at,
    header: "Date",
    cell: ({ row }) => when(row.original.created_at),
  },
  { id: "buyer", accessorFn: (o) => o.buyer_name, header: "Buyer" },
  {
    id: "package",
    accessorFn: (o) => o.package_name,
    header: "Package",
    cell: ({ row }) => (
      <span>
        {row.original.package_name}
        {row.original.quantity > 1 ? ` × ${row.original.quantity}` : ""}
        <span className="block text-xs text-muted-foreground">
          {row.original.total_passes} passes
        </span>
      </span>
    ),
  },
  {
    id: "amount",
    accessorFn: (o) => Number(o.amount),
    header: "Amount",
    cell: ({ row }) => (
      <span className="font-medium tabular-nums">{peso(Number(row.original.amount))}</span>
    ),
  },
  {
    id: "paidBy",
    accessorFn: (o) => paidBy(o) ?? "",
    header: "Paid by",
    cell: ({ row }) => paidBy(row.original) ?? "—",
  },
  {
    id: "status",
    accessorFn: (o) => o.status,
    header: "Status",
    cell: ({ row }) => (
      <span className={cn("font-medium", STATUS_TONE[row.original.status])}>
        {STATUS_LABEL[row.original.status] ?? row.original.status}
      </span>
    ),
  },
  { id: "cards", accessorFn: (o) => cardsLabel(o), header: "Cards" },
];

export function OrdersTab({
  isSuper,
  selected,
  onSelect,
}: {
  isSuper: boolean;
  selected: string | null;
  onSelect: (orderId: string | null) => void;
}) {
  const { data: orders = [], isLoading } = useGiftpassOrders();
  const order = orders.find((o) => o.id === selected) ?? null;

  const totals = useMemo(() => {
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const awaiting = orders.filter((o) => o.status === "awaiting_transfer");
    const paid = orders.filter(
      (o) => o.status === "paid" && o.paid_at && new Date(o.paid_at) >= monthStart,
    );
    const sum = (list: GiftpassOrder[]) => list.reduce((a, o) => a + Number(o.amount), 0);
    return {
      awaiting: sum(awaiting),
      awaitingCount: awaiting.length,
      paid: sum(paid),
      paidCount: paid.length,
      toPrint: orders.filter((o) => o.print_status === "to_print").length,
    };
  }, [orders]);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile layout="compact" label="Awaiting transfer" icon={ClockIcon}>
          <p className="text-2xl font-semibold text-warning">{peso(totals.awaiting)}</p>
          <p className="text-sm text-muted-foreground">
            {totals.awaitingCount} {totals.awaitingCount === 1 ? "order" : "orders"}
          </p>
        </StatTile>
        <StatTile layout="compact" label="Paid this month" icon={CoinsIcon}>
          <p className="text-2xl font-semibold">{peso(totals.paid)}</p>
          <p className="text-sm text-muted-foreground">
            {totals.paidCount} {totals.paidCount === 1 ? "order" : "orders"}
          </p>
        </StatTile>
        <StatTile layout="compact" label="Cards to print" icon={PrinterIcon}>
          <p className="text-2xl font-semibold">{totals.toPrint}</p>
          <p className="text-sm text-muted-foreground">orders in the print queue</p>
        </StatTile>
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-[999_1_560px]">
          <AdminDataTable
            data={orders}
            columns={columns}
            loading={isLoading}
            getRowId={(o) => o.id}
            searchText={(o) => `${o.order_code} ${o.buyer_name} ${o.buyer_email} ${o.buyer_phone}`}
            searchPlaceholder="Search order, buyer, email or phone"
            facets={[
              {
                id: "status",
                label: "Status",
                options: ORDER_FILTERS.filter((f) => f.id !== "all" && f.id !== "closed").map(
                  (f) => ({ value: f.id, label: f.label }),
                ),
                getValue: (o) => o.status,
              },
              {
                id: "source",
                label: "Type",
                options: [
                  { value: "online", label: "Online" },
                  { value: "manual", label: "Manual" },
                ],
                getValue: (o) => o.source,
              },
            ]}
            initialSorting={[{ id: "date", desc: true }]}
            exportColumns={[
              { header: "Order", value: (o) => o.order_code },
              { header: "Date", value: (o) => o.created_at },
              { header: "Type", value: (o) => o.source },
              { header: "Package", value: (o) => o.package_name },
              { header: "Quantity", value: (o) => o.quantity },
              { header: "Passes", value: (o) => o.total_passes },
              { header: "Amount", value: (o) => Number(o.amount) },
              { header: "Paid by", value: (o) => paidBy(o) },
              { header: "Status", value: (o) => STATUS_LABEL[o.status] ?? o.status },
              { header: "Cards", value: (o) => cardsLabel(o) },
            ]}
            exportFilename="giftpass-orders"
            exportScopeLabel="Order details only — no buyer contact details"
            emptyTitle="No orders yet"
            onRowClick={(o) => onSelect(o.id)}
            rowLabel={(o) => `Open ${o.order_code}`}
          />
        </div>
        {order ? (
          <OrderDetail order={order} isSuper={isSuper} onClose={() => onSelect(null)} />
        ) : null}
      </div>
    </div>
  );
}
